import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import AuthContext from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, CircularProgress, Paper,
    Tabs, Tab, Chip, IconButton, Tooltip, Autocomplete, TextField,
    ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
    Badge, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CakeIcon from '@mui/icons-material/Cake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTranslation } from 'react-i18next';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { elUpperCase, stripTonos } from '../utils/text';
import { Map as MapLibre, Source, Layer, Marker, Popup } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';

/* ── Unicode font loader ──────────────────────────────────────────────────
   Fetches NotoSans-Regular (supports Latin + Greek + Cyrillic + more) from
   jsDelivr CDN and caches the base64 result for the session.
   Falls back to helvetica silently if the fetch fails (offline / CORS).
   ──────────────────────────────────────────────────────────────────────── */

let _fontB64 = null;

async function loadUnicodeFont() {
    if (_fontB64) return _fontB64;
    try {
        const url = 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf';
        const res = await fetch(url);
        if (!res.ok) return null;
        const bytes = new Uint8Array(await res.arrayBuffer());
        let str = '';
        for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
        _fontB64 = btoa(str);
        return _fontB64;
    } catch { return null; }
}

function embedFont(doc, b64) {
    if (!b64) return 'helvetica';
    doc.addFileToVFS('NotoSans-Regular.ttf', b64);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
    doc.setFont('NotoSans', 'normal');
    return 'NotoSans';
}

const GREEN = '#10b981';
const ORANGE = '#f97316';
const RED = '#ef4444';
const PURPLE = '#8b5cf6';

function toISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return toISODate(d);
}

function getWeekEnd(weekStartISO) {
    const d = new Date(weekStartISO + 'T00:00:00');
    d.setDate(d.getDate() + 6);
    return toISODate(d);
}

function shiftDate(iso, days) {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return toISODate(d);
}

function shiftWeek(weekStartISO, weeks) {
    return shiftDate(weekStartISO, weeks * 7);
}

function formatDate(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

function getDayName(iso, locale) {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(locale === 'el' ? 'el-GR' : 'en-GB', { weekday: 'short' });
}

const SERVING_TEXT = { 0: '—', 1: '×1', 2: '×2', null: '' };

function StatusChip({ row, t }) {
    if (row.absent) return (
        <Chip icon={<PersonOffIcon sx={{ fontSize: 11 }} />} label={t('reports.status_absent')} size="small"
            sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(239,68,68,0.1)', color: RED, fontWeight: 600 }} />
    );
    if (row.arrived && row.departed) return (
        <Chip label={t('reports.status_in_out')} size="small"
            sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(16,185,129,0.1)', color: GREEN, fontWeight: 600 }} />
    );
    if (row.arrived) return (
        <Chip icon={<LoginIcon sx={{ fontSize: 11 }} />} label={t('reports.status_arrived')} size="small"
            sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(16,185,129,0.1)', color: GREEN, fontWeight: 600 }} />
    );
    if (row.departed) return (
        <Chip icon={<LogoutIcon sx={{ fontSize: 11 }} />} label={t('reports.status_departed')} size="small"
            sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(249,115,22,0.1)', color: ORANGE, fontWeight: 600 }} />
    );
    return <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>;
}

function ServingCell({ value }) {
    if (value === null || value === undefined || value === 0) {
        return <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>;
    }
    return <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: PURPLE, fontWeight: 600 }}>×{value}</Typography>;
}

const TH_STYLE = {
    fontFamily: '"Outfit", sans-serif',
    fontWeight: 600,
    fontSize: '0.72rem',
    color: '#64748b',
    py: 1,
    px: 1.5,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #e2e8f0',
};

const TD_STYLE = {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '0.82rem',
    py: 1,
    px: 1.5,
    borderBottom: '1px solid #f8fafc',
};

export default function ReportsPage() {
    const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    document.title = (pageTitle || 'App') + ' - Reports';

    const today = new Date();
    const tabParam = parseInt(searchParams.get('tab'));
    const [tab, setTab] = useState(isNaN(tabParam) ? 0 : tabParam);

    /* ── Daily report ── */
    const [dailyDate, setDailyDate] = useState(toISODate(today));
    const [dailyData, setDailyData] = useState(null);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [exportScope, setExportScope] = useState('all');
    const [studentDialogOpen, setStudentDialogOpen] = useState(false);
    const [studentDialogChild, setStudentDialogChild] = useState(null);

    /* ── Routes report ── */
    const [routeDate, setRouteDate] = useState(toISODate(today));
    const [routeData, setRouteData] = useState(null);
    const [routeLoading, setRouteLoading] = useState(false);

    /* ── Route replay ── */
    const [replayEnabled, setReplayEnabled] = useState(false);
    const [replayOpen, setReplayOpen] = useState(false);
    const [replayRoute, setReplayRoute] = useState(null); // { route_id, route_name }
    const [replayPoints, setReplayPoints] = useState([]);
    const [replayStops, setReplayStops] = useState([]);
    const [replayDelays, setReplayDelays] = useState([]);
    const [replayLoading, setReplayLoading] = useState(false);
    const [replayIdx, setReplayIdx] = useState(0);
    const [replayPlaying, setReplayPlaying] = useState(false);
    const [replayPopup, setReplayPopup] = useState(null); // stop object | null
    const replayTimerRef = useRef(null);
    const replayLogRef = useRef(null);
    const replayBarRef = useRef(null);
    const [replayScrubbing, setReplayScrubbing] = useState(false);
    const [replayDeleting, setReplayDeleting] = useState(false);
    const [replayDeleteConfirm, setReplayDeleteConfirm] = useState(false);

    /* auto-scroll the event log as replay advances */
    useEffect(() => {
        if (replayLogRef.current) {
            replayLogRef.current.scrollTop = replayLogRef.current.scrollHeight;
        }
    }, [replayIdx]);

    /* ── Birthdays report ── */
    const [birthdayData, setBirthdayData] = useState(null);
    const [birthdayLoading, setBirthdayLoading] = useState(false);

    /* ── Child report ── */
    const [allChildren, setAllChildren] = useState([]);
    const [selectedChild, setSelectedChild] = useState(null);
    const [period, setPeriod] = useState('weekly');
    const [weekStart, setWeekStart] = useState(getWeekStart(today));
    const [monthYear, setMonthYear] = useState({ year: today.getFullYear(), month: today.getMonth() });
    const [childData, setChildData] = useState(null);
    const [childLoading, setChildLoading] = useState(false);

    const authHeader = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
    }), [authTokens]);

    /* load children list for autocomplete */
    useEffect(() => {
        fetch(backendUrl + 'api/get-classes/', { headers: authHeader() })
            .then(r => { if (r.status === 401) { logoutUser(); return null; } return r.json(); })
            .then(data => {
                if (!data) return;
                const list = [];
                data.forEach(cls => {
                    cls.departments.forEach(dept => {
                        dept.children.forEach(child => {
                            list.push({
                                id: child.id,
                                name: `${child.firstname} ${child.lastname}`,
                                className: cls.name,
                                deptName: dept.name,
                                label: `${child.firstname} ${child.lastname}  (${cls.name} · ${dept.name})`,
                            });
                        });
                    });
                });
                list.sort((a, b) => a.name.localeCompare(b.name));
                setAllChildren(list);
            });
    }, [authHeader, backendUrl, logoutUser]);

    /* load daily report */
    const loadDailyReport = useCallback(async () => {
        setDailyLoading(true);
        setDailyData(null);
        setExportScope('all');
        const r = await fetch(backendUrl + `api/reports/daily/?date=${dailyDate}`, { headers: authHeader() });
        if (r.status === 401) { logoutUser(); return; }
        if (r.ok) setDailyData(await r.json());
        setDailyLoading(false);
    }, [dailyDate, authHeader, backendUrl, logoutUser]);

    useEffect(() => { loadDailyReport(); }, [loadDailyReport]);

    /* child date range */
    const getChildRange = useCallback(() => {
        if (period === 'weekly') {
            return { start: weekStart, end: getWeekEnd(weekStart) };
        }
        const { year, month } = monthYear;
        const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return { start, end };
    }, [period, weekStart, monthYear]);

    /* load child report */
    const loadChildReport = useCallback(async () => {
        if (!selectedChild) return;
        setChildLoading(true);
        setChildData(null);
        const { start, end } = getChildRange();
        const r = await fetch(backendUrl + `api/reports/child/${selectedChild.id}/?start=${start}&end=${end}`, { headers: authHeader() });
        if (r.status === 401) { logoutUser(); return; }
        if (r.ok) setChildData(await r.json());
        setChildLoading(false);
    }, [selectedChild, getChildRange, authHeader, backendUrl, logoutUser]);

    useEffect(() => { loadChildReport(); }, [loadChildReport]);

    /* load school settings for route replay flag */
    useEffect(() => {
        fetch(backendUrl + 'api/school/', { headers: authHeader() })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setReplayEnabled(!!data.enable_route_replay); })
            .catch(() => {});
    }, [authHeader, backendUrl]);

    /* load routes report */
    const loadRoutes = useCallback(async () => {
        setRouteLoading(true);
        setRouteData(null);
        const r = await fetch(backendUrl + `api/reports/routes/?date=${routeDate}`, { headers: authHeader() });
        if (r.status === 401) { logoutUser(); return; }
        if (r.ok) setRouteData(await r.json());
        setRouteLoading(false);
    }, [routeDate, authHeader, backendUrl, logoutUser]);

    useEffect(() => { if (tab === 2) loadRoutes(); }, [tab, loadRoutes]);

    /* replay: open modal and load points + stops */
    const openReplay = useCallback(async (route) => {
        setReplayRoute(route);
        setReplayPoints([]);
        setReplayStops([]);
        setReplayDelays([]);
        setReplayIdx(0);
        setReplayPlaying(false);
        setReplayOpen(true);
        setReplayLoading(true);
        const base = `route_id=${route.route_id}&date=${routeDate}`;
        const [rPoints, rStops] = await Promise.all([
            fetch(backendUrl + `api/reports/route-replay/?${base}`, { headers: authHeader() }),
            fetch(backendUrl + `api/reports/route-replay-stops/?${base}`, { headers: authHeader() }),
        ]);
        if (rPoints.ok) setReplayPoints(await rPoints.json());
        if (rStops.ok) {
            const stopsData = await rStops.json();
            setReplayStops(stopsData.stops ?? []);
            setReplayDelays(stopsData.delay_events ?? []);
        }
        setReplayLoading(false);
    }, [routeDate, authHeader, backendUrl]);

    /* replay: play/pause animation */
    useEffect(() => {
        if (replayPlaying && replayPoints.length > 0) {
            replayTimerRef.current = setInterval(() => {
                setReplayIdx(i => {
                    if (i >= replayPoints.length - 1) {
                        clearInterval(replayTimerRef.current);
                        setReplayPlaying(false);
                        return i;
                    }
                    return i + 1;
                });
            }, 100);
        } else {
            clearInterval(replayTimerRef.current);
        }
        return () => clearInterval(replayTimerRef.current);
    }, [replayPlaying, replayPoints.length]);

    const closeReplay = () => {
        setReplayOpen(false);
        setReplayPlaying(false);
        setReplayPopup(null);
        setReplayDelays([]);
        setReplayScrubbing(false);
        setReplayDeleteConfirm(false);
        clearInterval(replayTimerRef.current);
    };

    const deleteReplayPoints = useCallback(async () => {
        if (!replayDeleteConfirm) {
            setReplayDeleteConfirm(true);
            return;
        }
        setReplayDeleting(true);
        setReplayDeleteConfirm(false);
        const base = `route_id=${replayRoute.route_id}&date=${routeDate}`;
        const res = await fetch(backendUrl + `api/reports/route-replay-delete/?${base}`, {
            method: 'DELETE',
            headers: authHeader(),
        });
        setReplayDeleting(false);
        if (res.ok) {
            setReplayPoints([]);
            setReplayStops([]);
            setReplayDelays([]);
            setReplayIdx(0);
            setReplayPlaying(false);
        }
    }, [replayDeleteConfirm, replayRoute, routeDate, authHeader, backendUrl]);

    const seekTo = useCallback((clientX) => {
        if (!replayBarRef.current || replayPoints.length === 0) return;
        const rect = replayBarRef.current.getBoundingClientRect();
        const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        setReplayIdx(Math.round(fraction * (replayPoints.length - 1)));
    }, [replayPoints.length]);

    const handleBarMouseDown = (e) => {
        e.preventDefault();
        setReplayScrubbing(true);
        setReplayPlaying(false);
        seekTo(e.clientX);
    };

    useEffect(() => {
        if (!replayScrubbing) return;
        const onMove = (e) => seekTo(e.clientX);
        const onUp = () => setReplayScrubbing(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [replayScrubbing, seekTo]);

    /* load birthdays report — fetched on mount so badge count is immediately visible */
    const loadBirthdays = useCallback(async () => {
        setBirthdayLoading(true);
        const r = await fetch(backendUrl + 'api/reports/birthdays/', { headers: authHeader() });
        if (r.status === 401) { logoutUser(); return; }
        if (r.ok) setBirthdayData(await r.json());
        setBirthdayLoading(false);
    }, [authHeader, backendUrl, logoutUser]);

    useEffect(() => { loadBirthdays(); }, [loadBirthdays]);

    /* ── PDF: daily ── */
    const exportDailyPDF = async (scopeOverride) => {
        if (!dailyData) return;
        const scope = typeof scopeOverride === 'string' ? scopeOverride : exportScope;
        const fontB64 = await loadUnicodeFont();
        const doc = new jsPDF({ orientation: 'landscape' });
        const fontName = embedFont(doc, fontB64);
        const { date, school_name, classes } = dailyData;

        // Filter classes/departments based on scope
        let filteredClasses = classes;
        if (scope.startsWith('class:')) {
            const classId = scope.slice(6);
            filteredClasses = classes.filter(c => String(c.id) === classId);
        } else if (scope.startsWith('dept:')) {
            const [, classId, deptId] = scope.split(':');
            filteredClasses = classes
                .filter(c => String(c.id) === classId)
                .map(c => ({ ...c, departments: c.departments.filter(d => String(d.id) === deptId) }));
        }

        doc.setFont(fontName, 'normal');
        doc.setFontSize(16);
        doc.text(t('reports.pdf_daily_title'), 14, 16);
        doc.setFontSize(10);
        doc.text(`${t('reports.pdf_date')} ${formatDate(date)}`, 14, 23);
        if (school_name) doc.text(`${t('reports.pdf_school')} ${school_name}`, 14, 29);

        let y = school_name ? 35 : 30;

        filteredClasses.forEach(cls => {
            cls.departments.forEach(dept => {
                doc.setFont(fontName, 'normal');
                doc.setFontSize(11);
                doc.text(`${cls.name}  \u2014  ${dept.name}`, 14, y);
                y += 2;

                const head = [['#', t('reports.col_child'), t('reports.col_status'), t('reports.col_arrival'), t('reports.col_departure'), t('reports.col_breakfast'), t('reports.col_lunch'), t('reports.col_afternoon'), t('reports.col_slept'), t('reports.col_notes')]];
                const body = dept.children.map((child, i) => [
                    i + 1,
                    child.name,
                    child.absent ? t('reports.pdf_status_absent') : child.arrived ? (child.departed ? t('reports.pdf_status_in_out') : t('reports.pdf_status_arrived')) : '\u2014',
                    child.arrival_time || '\u2014',
                    child.departure_time || '\u2014',
                    child.breakfast > 0 ? `\u00d7${child.breakfast}` : '\u2014',
                    child.lunch > 0 ? `\u00d7${child.lunch}` : '\u2014',
                    child.afternoon > 0 ? `\u00d7${child.afternoon}` : '\u2014',
                    child.slept ? '\u2713' : '\u2014',
                    (child.notes || child.comments || '').substring(0, 60),
                ]);

                autoTable(doc, {
                    startY: y,
                    head,
                    body,
                    styles: { fontSize: 8, cellPadding: 2, font: fontName },
                    headStyles: { fillColor: [15, 45, 94], textColor: 255, font: fontName, fontStyle: 'normal' },
                    alternateRowStyles: { fillColor: [247, 249, 252] },
                    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 40 }, 9: { cellWidth: 50 } },
                    margin: { left: 14, right: 14 },
                });
                y = doc.lastAutoTable.finalY + 10;
                if (y > 185) { doc.addPage(); y = 14; }
            });
        });

        const scopeSuffix = scope === 'all' ? '' : `-${scope.replace(/:/g, '-')}`;
        doc.save(`attendance-report-${date}${scopeSuffix}.pdf`);
    };

    /* ── PDF: child ── */
    const exportChildPDF = async () => {
        if (!childData) return;
        const fontB64 = await loadUnicodeFont();
        const doc = new jsPDF();
        const fontName = embedFont(doc, fontB64);
        const { child_name, class_name, department_name, start, end, days } = childData;

        doc.setFont(fontName, 'normal');
        doc.setFontSize(15);
        doc.text(t('reports.pdf_child_title'), 14, 16);
        doc.setFontSize(10);
        doc.text(`${t('reports.pdf_child')} ${child_name}`, 14, 24);
        doc.text(`${t('reports.pdf_class')} ${class_name}  \u00b7  ${department_name}`, 14, 30);
        doc.text(`${t('reports.pdf_period')} ${formatDate(start)} \u2014 ${formatDate(end)}`, 14, 36);

        const head = [[t('reports.col_date'), t('reports.col_day'), t('reports.col_status'), t('reports.col_in'), t('reports.col_out'), t('reports.col_breakfast'), t('reports.col_lunch'), t('reports.col_afternoon'), t('reports.col_slept'), t('reports.col_comments')]];
        const body = days.map(day => [
            formatDate(day.date),
            getDayName(day.date, i18n.language),
            day.absent ? t('reports.pdf_status_absent') : day.arrived ? (day.departed ? t('reports.pdf_status_in_out') : t('reports.pdf_status_arrived')) : '\u2014',
            day.arrival_time || '\u2014',
            day.departure_time || '\u2014',
            day.has_log ? (day.breakfast > 0 ? `\u00d7${day.breakfast}` : '\u2014') : '',
            day.has_log ? (day.lunch > 0 ? `\u00d7${day.lunch}` : '\u2014') : '',
            day.has_log ? (day.afternoon > 0 ? `\u00d7${day.afternoon}` : '\u2014') : '',
            day.has_log ? (day.slept ? '\u2713' : '\u2014') : '',
            (day.comments || '').substring(0, 50),
        ]);

        autoTable(doc, {
            startY: 42,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 2, font: fontName },
            headStyles: { fillColor: [15, 45, 94], textColor: 255, font: fontName },
            alternateRowStyles: { fillColor: [247, 249, 252] },
            margin: { left: 14, right: 14 },
            didParseCell: (data) => {
                if (data.section === 'body' && days[data.row.index]?.absent) {
                    data.cell.styles.textColor = [200, 60, 60];
                }
            },
        });

        doc.save(`child-report-${child_name.replace(/\s+/g, '-')}-${start}.pdf`);
    };

    /* ── Daily stats ── */
    const dailyStats = dailyData ? (() => {
        let total = 0, arrived = 0, absent = 0;
        dailyData.classes.forEach(cls => cls.departments.forEach(dept => {
            dept.children.forEach(c => {
                total++;
                if (c.absent) absent++;
                else if (c.arrived) arrived++;
            });
        }));
        return { total, arrived, absent, pending: total - arrived - absent };
    })() : null;

    /* ── Month label ── */
    const monthLabel = new Date(monthYear.year, monthYear.month).toLocaleDateString(
        i18n.language === 'el' ? 'el-GR' : 'en-GB',
        { month: 'long', year: 'numeric' }
    );

    return (
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
            <Container maxWidth="xl">

                {/* Page header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 42, height: 42, borderRadius: '12px',
                        background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`,
                        boxShadow: '0 4px 14px rgba(15,45,94,0.25)', flexShrink: 0,
                    }}>
                        <AssessmentIcon sx={{ color: AMBER, fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2 }}>
                            {t('reports.title')}
                        </Typography>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: 0.1 }}>
                            {t('reports.subtitle')}
                        </Typography>
                    </Box>
                </Box>

                {/* Tabs */}
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        mb: 3,
                        '& .MuiTab-root': { fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                        '& .MuiTabs-indicator': { backgroundColor: AMBER },
                        '& .MuiTab-root.Mui-selected': { color: NAV_BG },
                    }}
                >
                    <Tab label={t('reports.tab_daily')} icon={<AssessmentIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                    <Tab label={t('reports.tab_child')} icon={<PersonIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                    <Tab label={t('reports.tab_routes')} icon={<DirectionsBusIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
                    <Tab
                        iconPosition="start"
                        icon={<CakeIcon sx={{ fontSize: 17 }} />}
                        label={
                            <Badge
                                badgeContent={birthdayData?.count || 0}
                                color="error"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700 } }}
                            >
                                <Box sx={{ pr: birthdayData?.count ? 1.5 : 0 }}>{t('reports.tab_birthdays')}</Box>
                            </Badge>
                        }
                    />
                </Tabs>

                {/* ═══ DAILY REPORT TAB ═══ */}
                {tab === 0 && (
                    <Box>
                        {/* Controls */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => setDailyDate(d => shiftDate(d, -1))}
                                    sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <TextField
                                    type="date" size="small" label={t('reports.date_label')} value={dailyDate}
                                    onChange={(e) => setDailyDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 160, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }}
                                />
                                <IconButton size="small" onClick={() => setDailyDate(d => shiftDate(d, 1))}
                                    sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>

                            {dailyLoading && <CircularProgress size={20} thickness={3} sx={{ color: AMBER }} />}

                            {dailyData && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <Select
                                            value={exportScope}
                                            onChange={(e) => setExportScope(e.target.value)}
                                            displayEmpty
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: AMBER }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}
                                        >
                                            <MenuItem value="all" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}>{t('reports.all_classes')}</MenuItem>
                                            {dailyData.classes.map(cls => [
                                                <MenuItem key={`class:${cls.id}`} value={`class:${cls.id}`} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: NAV_BG }}>
                                                    {cls.name}
                                                </MenuItem>,
                                                ...cls.departments.map(dept => (
                                                    <MenuItem key={`dept:${cls.id}:${dept.id}`} value={`dept:${cls.id}:${dept.id}`} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', pl: 3, color: '#475569' }}>
                                                        {cls.name} › {dept.name}
                                                    </MenuItem>
                                                )),
                                            ])}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title={t('reports.export_tooltip')} arrow>
                                        <Button
                                            variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                                            onClick={exportDailyPDF}
                                            sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', borderRadius: '8px', '&:hover': { borderColor: AMBER, color: AMBER, background: AMBER_GLOW } }}
                                        >
                                            {t('reports.export_pdf')}
                                        </Button>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>

                        {/* Stats chips */}
                        {dailyStats && (
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                                <Chip label={t('reports.total', { count: dailyStats.total })} size="small"
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(15,45,94,0.08)', color: NAV_BG }} />
                                <Chip icon={<LoginIcon sx={{ fontSize: 13 }} />} label={t('reports.stat_arrived', { count: dailyStats.arrived })} size="small"
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: GREEN }} />
                                <Chip icon={<PersonOffIcon sx={{ fontSize: 13 }} />} label={t('reports.stat_absent', { count: dailyStats.absent })} size="small"
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: RED }} />
                                <Chip label={t('reports.stat_pending', { count: dailyStats.pending })} size="small"
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(100,116,139,0.08)', color: '#64748b' }} />
                            </Box>
                        )}

                        {/* Tables grouped by class → department */}
                        {dailyData && dailyData.classes.map(cls => (
                            <Box key={cls.id} sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography sx={{
                                        fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.05rem',
                                        color: NAV_BG, px: 0.5,
                                        borderLeft: `3px solid ${AMBER}`, pl: 1.5,
                                    }}>
                                        {cls.name}
                                    </Typography>
                                    <Tooltip title={t('reports.export_tooltip')} arrow>
                                        <IconButton size="small" onClick={() => exportDailyPDF(`class:${cls.id}`)}
                                            sx={{ color: '#94a3b8', '&:hover': { color: AMBER } }}>
                                            <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {cls.departments.map(dept => (
                                    <Paper key={dept.id} elevation={0} sx={{ mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,45,94,0.06)' }}>
                                        <Box sx={{ px: 3, py: 1.5, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: 'rgba(241,245,249,0.9)', letterSpacing: '0.04em' }}>
                                                {elUpperCase(dept.name)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={t('reports.n_children', { count: dept.children.length })}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(245,158,11,0.2)', color: AMBER, fontWeight: 600 }}
                                                />
                                                <Tooltip title={t('reports.export_tooltip')} arrow>
                                                    <IconButton size="small" onClick={() => exportDailyPDF(`dept:${cls.id}:${dept.id}`)}
                                                        sx={{ color: 'rgba(241,245,249,0.5)', p: 0.5, '&:hover': { color: AMBER, background: 'rgba(245,158,11,0.1)' } }}>
                                                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ background: '#f8fafc' }}>
                                                        {[t('reports.col_child'), t('reports.col_status'), t('reports.col_arrival'), t('reports.col_departure'), t('reports.col_breakfast'), t('reports.col_lunch'), t('reports.col_afternoon'), t('reports.col_slept'), t('reports.col_notes')].map(h => (
                                                            <TableCell key={h} sx={TH_STYLE}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dept.children.map(child => (
                                                        <TableRow key={child.id} sx={{
                                                            background: child.absent ? 'rgba(239,68,68,0.06)' : 'inherit',
                                                            '&:hover': { background: child.absent ? 'rgba(239,68,68,0.1)' : 'rgba(15,45,94,0.02)' },
                                                            '&:last-child td': { borderBottom: 'none' },
                                                        }}>
                                                            <TableCell sx={{ ...TD_STYLE, fontWeight: 600, color: NAV_BG, cursor: 'pointer', '&:hover': { color: AMBER, textDecoration: 'underline' } }}
                                                            onClick={() => { setStudentDialogChild(child); setStudentDialogOpen(true); }}>
                                                            {child.name}
                                                        </TableCell>
                                                            <TableCell sx={TD_STYLE}><StatusChip row={child} t={t} /></TableCell>
                                                            <TableCell sx={{ ...TD_STYLE, color: GREEN, fontWeight: 600 }}>{child.arrival_time || '—'}</TableCell>
                                                            <TableCell sx={{ ...TD_STYLE, color: ORANGE, fontWeight: 600 }}>{child.departure_time || '—'}</TableCell>
                                                            <TableCell sx={TD_STYLE}><ServingCell value={child.breakfast} /></TableCell>
                                                            <TableCell sx={TD_STYLE}><ServingCell value={child.lunch} /></TableCell>
                                                            <TableCell sx={TD_STYLE}><ServingCell value={child.afternoon} /></TableCell>
                                                            <TableCell sx={TD_STYLE}>
                                                                {child.slept
                                                                    ? <BedtimeIcon sx={{ fontSize: 16, color: PURPLE }} />
                                                                    : <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>
                                                                }
                                                            </TableCell>
                                                            <TableCell sx={{ ...TD_STYLE, color: '#64748b', maxWidth: 220 }}>
                                                                {child.notes || child.comments || ''}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                ))}
                            </Box>
                        ))}

                        {!dailyLoading && dailyData && dailyData.classes.length === 0 && (
                            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8' }}>{t('reports.no_students')}</Typography>
                            </Paper>
                        )}
                    </Box>
                )}

                {/* ═══ CHILD REPORT TAB ═══ */}
                {tab === 1 && (
                    <Box>
                        {/* Controls */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Autocomplete
                                options={allChildren}
                                getOptionLabel={(o) => o.label}
                                value={selectedChild}
                                onChange={(_, v) => { setSelectedChild(v); setChildData(null); }}
                                size="small"
                                sx={{ minWidth: 320 }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('reports.select_child')}
                                        sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }}
                                    />
                                )}
                            />

                            <ToggleButtonGroup value={period} exclusive onChange={(_, v) => { if (v) setPeriod(v); }} size="small">
                                <ToggleButton value="weekly" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 2, '&.Mui-selected': { background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG } }}>
                                    {t('reports.weekly')}
                                </ToggleButton>
                                <ToggleButton value="monthly" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 2, '&.Mui-selected': { background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG } }}>
                                    {t('reports.monthly')}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {/* Period navigation */}
                            {period === 'weekly' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => setWeekStart(w => shiftWeek(w, -1))}
                                        sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#475569', fontWeight: 500, minWidth: 210, textAlign: 'center' }}>
                                        {formatDate(weekStart)} — {formatDate(getWeekEnd(weekStart))}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setWeekStart(w => shiftWeek(w, 1))}
                                        sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => setMonthYear(my => my.month === 0 ? { year: my.year - 1, month: 11 } : { ...my, month: my.month - 1 })}
                                        sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#475569', fontWeight: 500, minWidth: 150, textAlign: 'center' }}>
                                        {monthLabel}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setMonthYear(my => my.month === 11 ? { year: my.year + 1, month: 0 } : { ...my, month: my.month + 1 })}
                                        sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            )}

                            {childLoading && <CircularProgress size={20} thickness={3} sx={{ color: AMBER }} />}

                            {childData && (
                                <Tooltip title={t('reports.export_tooltip')} arrow>
                                    <Button
                                        variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                                        onClick={exportChildPDF}
                                        sx={{ ml: 'auto', fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', borderRadius: '8px', '&:hover': { borderColor: AMBER, color: AMBER, background: AMBER_GLOW } }}
                                    >
                                        {t('reports.export_pdf')}
                                    </Button>
                                </Tooltip>
                            )}
                        </Box>

                        {!selectedChild && (
                            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <PersonIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.95rem' }}>
                                    {t('reports.select_child_hint')}
                                </Typography>
                            </Paper>
                        )}

                        {selectedChild && childData && (() => {
                            const arrivedCount = childData.days.filter(d => d.arrived).length;
                            const absentCount = childData.days.filter(d => d.absent).length;
                            const totalDays = childData.days.length;

                            return (
                                <>
                                    {/* Child summary chips */}
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip label={childData.class_name} size="small"
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(15,45,94,0.08)', color: NAV_BG }} />
                                        <Chip label={childData.department_name} size="small"
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: `rgba(245,158,11,0.12)`, color: AMBER_DARK }} />
                                        <Chip icon={<LoginIcon sx={{ fontSize: 13 }} />} label={t('reports.days_present', { count: arrivedCount, total: totalDays })} size="small"
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: GREEN }} />
                                        {absentCount > 0 && (
                                            <Chip icon={<PersonOffIcon sx={{ fontSize: 13 }} />} label={t('reports.stat_absent', { count: absentCount })} size="small"
                                                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: RED }} />
                                        )}
                                    </Box>

                                    <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,45,94,0.06)' }}>
                                        <Box sx={{ px: 3, py: 1.5, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)` }}>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: 'rgba(241,245,249,0.9)', letterSpacing: '0.04em' }}>
                                                {elUpperCase(childData.child_name)}
                                            </Typography>
                                        </Box>

                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ background: '#f8fafc' }}>
                                                        {[t('reports.col_date'), t('reports.col_day'), t('reports.col_status'), t('reports.col_arrival'), t('reports.col_departure'), t('reports.col_breakfast'), t('reports.col_lunch'), t('reports.col_afternoon'), t('reports.col_slept'), t('reports.col_comments')].map(h => (
                                                            <TableCell key={h} sx={TH_STYLE}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {childData.days.map(day => {
                                                        const d = new Date(day.date + 'T00:00:00');
                                                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                                        return (
                                                            <TableRow key={day.date} sx={{
                                                                background: day.absent ? 'rgba(239,68,68,0.06)' : isWeekend ? 'rgba(100,116,139,0.04)' : 'transparent',
                                                                '&:hover': { background: day.absent ? 'rgba(239,68,68,0.1)' : 'rgba(15,45,94,0.025)' },
                                                                '&:last-child td': { borderBottom: 'none' },
                                                            }}>
                                                                <TableCell sx={{ ...TD_STYLE, fontWeight: 600, color: NAV_BG, whiteSpace: 'nowrap' }}>
                                                                    {formatDate(day.date)}
                                                                </TableCell>
                                                                <TableCell sx={{ ...TD_STYLE, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                                    {getDayName(day.date, i18n.language)}
                                                                </TableCell>
                                                                <TableCell sx={TD_STYLE}><StatusChip row={day} t={t} /></TableCell>
                                                                <TableCell sx={{ ...TD_STYLE, color: GREEN, fontWeight: 600 }}>{day.arrival_time || '—'}</TableCell>
                                                                <TableCell sx={{ ...TD_STYLE, color: ORANGE, fontWeight: 600 }}>{day.departure_time || '—'}</TableCell>
                                                                <TableCell sx={TD_STYLE}>{day.has_log ? <ServingCell value={day.breakfast} /> : ''}</TableCell>
                                                                <TableCell sx={TD_STYLE}>{day.has_log ? <ServingCell value={day.lunch} /> : ''}</TableCell>
                                                                <TableCell sx={TD_STYLE}>{day.has_log ? <ServingCell value={day.afternoon} /> : ''}</TableCell>
                                                                <TableCell sx={TD_STYLE}>
                                                                    {day.has_log
                                                                        ? (day.slept
                                                                            ? <BedtimeIcon sx={{ fontSize: 16, color: PURPLE }} />
                                                                            : <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>)
                                                                        : ''}
                                                                </TableCell>
                                                                <TableCell sx={{ ...TD_STYLE, color: '#64748b' }}>{day.comments || ''}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </>
                            );
                        })()}
                    </Box>
                )}

                {/* ═══ ROUTES TAB ═══ */}
                {tab === 2 && (
                    <Box>
                        {/* Controls */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => setRouteDate(d => shiftDate(d, -1))}
                                    sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <TextField
                                    type="date" size="small" label={t('reports.date_label')} value={routeDate}
                                    onChange={(e) => setRouteDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 160, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }}
                                />
                                <IconButton size="small" onClick={() => setRouteDate(d => shiftDate(d, 1))}
                                    sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                            <Button variant="outlined" size="small" onClick={loadRoutes}
                                sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', borderRadius: '8px', '&:hover': { borderColor: AMBER, color: AMBER, background: AMBER_GLOW } }}>
                                {i18n.language === 'el' ? 'Ανανέωση' : 'Refresh'}
                            </Button>
                            {routeLoading && <CircularProgress size={20} thickness={3} sx={{ color: AMBER }} />}
                        </Box>

                        {!routeLoading && routeData && routeData.length === 0 && (
                            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <DirectionsBusIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.95rem' }}>
                                    {i18n.language === 'el'
                                        ? `Δεν υπάρχουν καταγεγραμμένες διαδρομές για ${formatDate(routeDate)}.`
                                        : `No routes recorded for ${formatDate(routeDate)}.`}
                                </Typography>
                            </Paper>
                        )}

                        {routeData && routeData.map(route => {
                            const routeTypeLabel = [
                                i18n.language === 'el' ? 'Προς σχολείο' : 'To School',
                                i18n.language === 'el' ? 'Από σχολείο' : 'From School',
                                i18n.language === 'el' ? 'Μία φορά' : 'One Time',
                            ][route.route_type] || '';
                            const isDropoff = route.route_type === 1;
                            const stopLabel = isDropoff
                                ? (i18n.language === 'el' ? 'Αποβίβαση' : 'Drop-off')
                                : (i18n.language === 'el' ? 'Παραλαβή' : 'Pick-up');
                            const fmtTime = (iso) => iso
                                ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                                : '—';
                            const fmtDuration = (secs) => {
                                if (secs === null || secs === undefined) return '—';
                                const m = Math.floor(secs / 60);
                                const s = secs % 60;
                                return m > 0 ? `${m}m ${s}s` : `${s}s`;
                            };
                            return (
                                <Box key={route.route_id} sx={{ mb: 4 }}>
                                    <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,45,94,0.06)' }}>
                                        {/* Route header */}
                                        <Box sx={{ px: 3, py: 1.5, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <DirectionsBusIcon sx={{ color: AMBER, fontSize: 18 }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9' }}>
                                                    {route.route_name}
                                                </Typography>
                                                <Chip
                                                    label={routeTypeLabel}
                                                    size="small"
                                                    sx={{ height: 19, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: 'rgba(245,158,11,0.18)', color: AMBER }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                {route.driver_firstname && (
                                                    <Chip
                                                        label={`${route.driver_firstname} ${route.driver_lastname}`}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: 'rgba(100,116,139,0.18)', color: '#f1f5f9' }}
                                                    />
                                                )}
                                                <Chip
                                                    icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                                                    label={`${route.stats.total_stops} ${stopLabel.toLowerCase()}s`}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: 'rgba(16,185,129,0.18)', color: GREEN }}
                                                />
                                                {route.stats.avg_seconds_between_stops !== null && (
                                                    <Chip
                                                        icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                                        label={`avg ${Math.round(route.stats.avg_seconds_between_stops / 60)} min`}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: 'rgba(245,158,11,0.15)', color: AMBER_DARK }}
                                                    />
                                                )}
                                                {replayEnabled && (() => {
                                                    const hasPoints = (route.stats.point_count ?? 0) > 0;
                                                    return (
                                                        <Chip
                                                            icon={<HistoryIcon sx={{ fontSize: 12 }} />}
                                                            label={t('reports.replay_btn')}
                                                            size="small"
                                                            onClick={hasPoints ? () => openReplay(route) : undefined}
                                                            disabled={!hasPoints}
                                                            sx={{ height: 20, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, ...( hasPoints ? { backgroundColor: 'rgba(139,92,246,0.18)', color: PURPLE, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(139,92,246,0.3)' } } : { cursor: 'default' }) }}
                                                        />
                                                    );
                                                })()}
                                            </Box>
                                        </Box>

                                        {/* Timing summary */}
                                        <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', background: '#fafcff' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <AccessTimeIcon sx={{ fontSize: 15, color: GREEN }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#475569' }}>
                                                    <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                                                        {i18n.language === 'el' ? 'Εκκίνηση:' : 'Started:'}
                                                    </Box>{' '}
                                                    <Box component="span" sx={{ color: GREEN, fontWeight: 700 }}>{fmtTime(route.started_at)}</Box>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <AccessTimeIcon sx={{ fontSize: 15, color: ORANGE }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#475569' }}>
                                                    <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                                                        {i18n.language === 'el' ? 'Ολοκλήρωση:' : 'Finished:'}
                                                    </Box>{' '}
                                                    <Box component="span" sx={{ color: ORANGE, fontWeight: 700 }}>{fmtTime(route.finished_at)}</Box>
                                                </Typography>
                                            </Box>
                                            {route.duration_seconds !== null && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <AccessTimeIcon sx={{ fontSize: 15, color: PURPLE }} />
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#475569' }}>
                                                        <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                                                            {i18n.language === 'el' ? 'Διάρκεια:' : 'Duration:'}
                                                        </Box>{' '}
                                                        <Box component="span" sx={{ color: PURPLE, fontWeight: 700 }}>{fmtDuration(route.duration_seconds)}</Box>
                                                    </Typography>
                                                </Box>
                                            )}
                                            {route.stats.avg_seconds_between_stops !== null && (
                                                <Box sx={{ display: 'flex', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                                        <strong>{i18n.language === 'el' ? 'Μέσος:' : 'Avg:'}</strong> {Math.round(route.stats.avg_seconds_between_stops / 60)} min
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                                        <strong>{i18n.language === 'el' ? 'Ελάχ:' : 'Min:'}</strong> {Math.round(route.stats.min_seconds_between_stops / 60)} min
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                                        <strong>{i18n.language === 'el' ? 'Μέγ:' : 'Max:'}</strong> {Math.round(route.stats.max_seconds_between_stops / 60)} min
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Delay events */}
                                        {route.delay_events && route.delay_events.length > 0 && (
                                            <Box sx={{ px: 3, py: 1.25, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', background: '#fffbf5' }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: ORANGE, mr: 0.5 }} />
                                                {route.delay_events.map((de, i) => {
                                                    const cancelled = de.delay_minutes === null;
                                                    const early = de.delay_minutes < 0;
                                                    const color = cancelled ? '#64748b' : early ? '#16a34a' : ORANGE;
                                                    const label = cancelled
                                                        ? (i18n.language === 'el' ? 'Ακυρώθηκε' : 'Cancelled')
                                                        : early
                                                            ? (i18n.language === 'el' ? `${Math.abs(de.delay_minutes)}λ νωρίτερα` : `${Math.abs(de.delay_minutes)}m early`)
                                                            : (i18n.language === 'el' ? `+${de.delay_minutes}λ καθυστέρηση` : `+${de.delay_minutes}m delay`);
                                                    const timeStr = new Date(de.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                    return (
                                                        <Chip key={i}
                                                            icon={<AccessTimeIcon sx={{ fontSize: 11, color: `${color} !important` }} />}
                                                            label={`${label} · ${timeStr}`}
                                                            size="small"
                                                            sx={{ height: 19, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: color + '18', color }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        {/* Stops table */}
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ background: '#f8fafc' }}>
                                                        {['#', i18n.language === 'el' ? 'Παιδί' : 'Child', i18n.language === 'el' ? 'Ώρα' : 'Time', i18n.language === 'el' ? 'Κενό' : 'Gap'].map(h => (
                                                            <TableCell key={h} sx={TH_STYLE}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {route.stops.map((stop, idx) => (
                                                        <TableRow key={stop.pickup_id} sx={{
                                                            '&:hover': { background: 'rgba(15,45,94,0.02)' },
                                                            '&:last-child td': { borderBottom: 'none' },
                                                        }}>
                                                            <TableCell sx={{ ...TD_STYLE, width: 50 }}>
                                                                <Box sx={{
                                                                    width: 24, height: 24, borderRadius: '50%',
                                                                    backgroundColor: AMBER + '22', color: AMBER_DARK,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontWeight: 700, fontSize: 11, fontFamily: '"Outfit", sans-serif',
                                                                }}>
                                                                    {idx + 1}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ ...TD_STYLE, fontWeight: 600, color: NAV_BG }}>
                                                                {stop.child_firstname} {stop.child_lastname}
                                                            </TableCell>
                                                            <TableCell sx={{ ...TD_STYLE, color: GREEN, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                {fmtTime(stop.timestamp)}
                                                            </TableCell>
                                                            <TableCell sx={{ ...TD_STYLE }}>
                                                                {stop.seconds_since_prev !== null
                                                                    ? <Chip
                                                                        label={stop.seconds_since_prev < 60
                                                                            ? `${stop.seconds_since_prev}s`
                                                                            : `${Math.floor(stop.seconds_since_prev / 60)}m ${stop.seconds_since_prev % 60}s`}
                                                                        size="small"
                                                                        sx={{ height: 18, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, backgroundColor: '#f1f5f9', color: '#475569' }}
                                                                    />
                                                                    : <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* ═══ BIRTHDAYS TAB ═══ */}
                {tab === 3 && (
                    <Box>
                        {birthdayLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={28} thickness={3} sx={{ color: AMBER }} />
                            </Box>
                        )}
                        {!birthdayLoading && birthdayData && birthdayData.count === 0 && (
                            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <CakeIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.95rem' }}>
                                    {t('reports.birthday_no_birthdays')}
                                </Typography>
                            </Paper>
                        )}
                        {!birthdayLoading && birthdayData && birthdayData.count > 0 && (
                            <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,45,94,0.06)' }}>
                                <Box sx={{ px: 3, py: 1.5, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CakeIcon sx={{ color: AMBER, fontSize: 18 }} />
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9' }}>
                                        {t('reports.birthday_tab_title')} {formatDate(birthdayData.date)}
                                    </Typography>
                                    <Chip label={birthdayData.count} size="small" sx={{ ml: 'auto', height: 22, fontSize: '0.72rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, background: AMBER, color: NAV_BG }} />
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ background: '#f8fafc' }}>
                                                {[t('reports.birthday_col_name'), t('reports.birthday_col_class'), t('reports.birthday_col_department'), t('reports.birthday_col_dob')].map(h => (
                                                    <TableCell key={h} sx={TH_STYLE}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {birthdayData.children.map(child => (
                                                <TableRow key={child.id} sx={{ '&:hover': { background: 'rgba(15,45,94,0.02)' }, '&:last-child td': { borderBottom: 'none' } }}>
                                                    <TableCell sx={{ ...TD_STYLE, fontWeight: 600, color: NAV_BG }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CakeIcon sx={{ fontSize: 15, color: AMBER }} />
                                                            {child.firstname} {child.lastname}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={TD_STYLE}>{child.class_name || '—'}</TableCell>
                                                    <TableCell sx={TD_STYLE}>{child.department_name || '—'}</TableCell>
                                                    <TableCell sx={{ ...TD_STYLE, color: '#64748b' }}>{child.date_of_birth ? formatDate(child.date_of_birth) : '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                )}

            </Container>

            {/* ═══ ROUTE REPLAY MODAL ═══ */}
            <Dialog open={replayOpen} onClose={closeReplay} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1rem', color: NAV_BG, borderBottom: '1px solid #e2e8f0', py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ color: PURPLE, fontSize: 20 }} />
                    {t('reports.replay_title')} — {replayRoute?.route_name}
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {replayLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, gap: 1.5 }}>
                            <CircularProgress size={24} thickness={3} sx={{ color: PURPLE }} />
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontSize: '0.9rem' }}>
                                {t('reports.replay_loading')}
                            </Typography>
                        </Box>
                    )}
                    {!replayLoading && replayPoints.length === 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.9rem' }}>
                                {t('reports.replay_no_points')}
                            </Typography>
                        </Box>
                    )}
                    {!replayLoading && replayPoints.length > 0 && (() => {
                        const visibleCoords = replayPoints.slice(0, replayIdx + 1).map(p => [p.lng, p.lat]);
                        const current = replayPoints[replayIdx];
                        const currentTs = current.ts;
                        const allLngs = replayPoints.map(p => p.lng);
                        const allLats = replayPoints.map(p => p.lat);
                        const midLng = (Math.min(...allLngs) + Math.max(...allLngs)) / 2;
                        const midLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
                        const trackGeo = {
                            type: 'Feature',
                            geometry: { type: 'LineString', coordinates: replayPoints.map(p => [p.lng, p.lat]) },
                        };
                        const visibleGeo = {
                            type: 'Feature',
                            geometry: { type: 'LineString', coordinates: visibleCoords },
                        };
                        const dotGeo = {
                            type: 'Feature',
                            geometry: { type: 'Point', coordinates: [current.lng, current.lat] },
                        };
                        const timeLabel = new Date(currentTs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

                        /* build sorted event log of things that have happened so far */
                        const logEvents = [];
                        replayStops.forEach(stop => {
                            if (stop.absent) {
                                logEvents.push({ stop, type: 'absent', ts: null });
                            } else if (stop.pickup_ts && currentTs >= stop.pickup_ts) {
                                logEvents.push({ stop, type: 'pickup', ts: stop.pickup_ts });
                            } else if (stop.skip_ts && currentTs >= stop.skip_ts) {
                                logEvents.push({ stop, type: 'skip', ts: stop.skip_ts });
                            }
                        });
                        replayDelays.forEach(de => {
                            if (currentTs >= de.ts) {
                                logEvents.push({ type: 'delay', ts: de.ts, delay_minutes: de.delay_minutes });
                            }
                        });
                        logEvents.sort((a, b) => {
                            if (!a.ts && !b.ts) return (a.stop?.stop_index ?? 0) - (b.stop?.stop_index ?? 0);
                            if (!a.ts) return -1;
                            if (!b.ts) return 1;
                            return a.ts.localeCompare(b.ts);
                        });

                        const popupStop = replayPopup;

                        return (
                            <Box sx={{ display: 'flex', height: 480 }}>
                                {/* ── Map ── */}
                                <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
                                    <MapLibre
                                        initialViewState={{ longitude: midLng, latitude: midLat, zoom: 12 }}
                                        style={{ width: '100%', height: '100%' }}
                                        mapStyle="https://tiles.openfreemap.org/styles/positron"
                                    >
                                        {/* Full ghost track */}
                                        <Source id="track-full" type="geojson" data={trackGeo}>
                                            <Layer id="track-full-line" type="line"
                                                paint={{ 'line-color': '#cbd5e1', 'line-width': 3, 'line-dasharray': [2, 2] }} />
                                        </Source>
                                        {/* Played portion */}
                                        <Source id="track-played" type="geojson" data={visibleGeo}>
                                            <Layer id="track-played-line" type="line"
                                                paint={{ 'line-color': PURPLE, 'line-width': 4 }} />
                                        </Source>
                                        {/* Current position dot */}
                                        <Source id="dot" type="geojson" data={dotGeo}>
                                            <Layer id="dot-layer" type="circle"
                                                paint={{ 'circle-radius': 8, 'circle-color': AMBER, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 }} />
                                        </Source>
                                        {/* Child stop markers */}
                                        {replayStops.map(stop => {
                                            const pickedUp = stop.pickup_ts && currentTs >= stop.pickup_ts;
                                            const skipped = stop.skip_ts && currentTs >= stop.skip_ts;
                                            const bg = stop.absent ? '#9ca3af'
                                                : pickedUp ? '#16a34a'
                                                : skipped ? '#f59e0b'
                                                : '#f97316';
                                            const label = stop.absent ? '✕' : stop.firstname.charAt(0).toUpperCase();
                                            return (
                                                <Marker key={stop.child_id} longitude={stop.lng} latitude={stop.lat} anchor="center">
                                                    <Box
                                                        onMouseEnter={() => setReplayPopup(stop)}
                                                        onMouseLeave={() => setReplayPopup(null)}
                                                        sx={{
                                                            width: 20, height: 20, borderRadius: '50%',
                                                            background: bg, border: '1.5px solid #fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '9px', fontWeight: 700, color: '#fff',
                                                            fontFamily: '"Outfit", sans-serif',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                            opacity: (pickedUp || skipped) ? 0.7 : 1,
                                                            cursor: 'default',
                                                        }}>
                                                        {label}
                                                    </Box>
                                                </Marker>
                                            );
                                        })}
                                        {/* Child info popup */}
                                        {popupStop && (() => {
                                            const pickedUp = popupStop.pickup_ts && currentTs >= popupStop.pickup_ts;
                                            const skipped = popupStop.skip_ts && currentTs >= popupStop.skip_ts;
                                            const statusColor = popupStop.absent ? '#6b7280' : pickedUp ? '#16a34a' : skipped ? '#d97706' : '#f97316';
                                            const statusLabel = popupStop.absent ? 'Absent'
                                                : pickedUp ? `Picked up at ${new Date(popupStop.pickup_ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                : skipped ? `Skipped at ${new Date(popupStop.skip_ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                : 'Waiting';
                                            return (
                                                <Popup longitude={popupStop.lng} latitude={popupStop.lat}
                                                    anchor="top" offset={16}
                                                    closeButton={false} closeOnClick={false}
                                                    style={{ fontFamily: '"Outfit", sans-serif' }}>
                                                    <Box sx={{ minWidth: 140, p: 0.5 }}>
                                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                                                            {popupStop.firstname} {popupStop.lastname}
                                                        </Typography>
                                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                                                            Stop #{popupStop.stop_index + 1}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.75, display: 'inline-block', px: 1, py: 0.25, borderRadius: '6px', background: statusColor + '20' }}>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600, color: statusColor }}>
                                                                {statusLabel}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Popup>
                                            );
                                        })()}
                                    </MapLibre>
                                    {/* Current time overlay */}
                                    <Box sx={{
                                        position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)',
                                        background: 'rgba(15,23,42,0.75)', color: '#fff', borderRadius: '8px',
                                        px: 1.5, py: 0.5, fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem',
                                        fontWeight: 600, letterSpacing: '0.05em', pointerEvents: 'none',
                                    }}>
                                        {timeLabel}
                                    </Box>
                                    {/* Progress bar / scrubber */}
                                    <Box
                                        ref={replayBarRef}
                                        onMouseDown={handleBarMouseDown}
                                        sx={{
                                            position: 'absolute', bottom: 0, left: 0, right: 0,
                                            height: 6, background: '#e2e8f0',
                                            cursor: replayPoints.length > 0 ? 'pointer' : 'default',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box sx={{
                                            height: '100%', background: PURPLE, position: 'relative',
                                            width: `${replayPoints.length > 0 ? ((replayIdx + 1) / replayPoints.length) * 100 : 0}%`,
                                            transition: replayScrubbing ? 'none' : 'width 0.1s linear',
                                        }}>
                                            {replayPoints.length > 0 && (
                                                <Box sx={{
                                                    position: 'absolute', right: -5, top: '50%', transform: 'translateY(-50%)',
                                                    width: 10, height: 10, borderRadius: '50%', background: PURPLE,
                                                    boxShadow: '0 0 0 2px #fff',
                                                }} />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* ── Event log panel ── */}
                                <Box ref={replayLogRef} sx={{
                                    width: 240, flexShrink: 0, borderLeft: '1px solid #e2e8f0',
                                    overflowY: 'auto', background: '#f8fafc', display: 'flex', flexDirection: 'column',
                                }}>
                                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Route Log
                                        </Typography>
                                    </Box>
                                    {logEvents.length === 0 ? (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#cbd5e1', textAlign: 'center' }}>
                                                Events will appear here as the route plays
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {logEvents.map((ev, i) => {
                                                if (ev.type === 'delay') {
                                                    const cancelled = ev.delay_minutes === null;
                                                    const early = ev.delay_minutes < 0;
                                                    const color = cancelled ? '#64748b' : early ? '#16a34a' : '#f97316';
                                                    const label = cancelled
                                                        ? (i18n.language === 'el' ? 'Καθυστέρηση ακυρώθηκε' : 'Delay cancelled')
                                                        : early
                                                            ? (i18n.language === 'el' ? `${Math.abs(ev.delay_minutes)} λεπτά νωρίτερα` : `${Math.abs(ev.delay_minutes)} min early`)
                                                            : (i18n.language === 'el' ? `Καθυστέρηση ${ev.delay_minutes} λεπτών` : `${ev.delay_minutes} min delay`);
                                                    const timeStr = new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                    return (
                                                        <Box key={i} sx={{
                                                            display: 'flex', alignItems: 'flex-start', gap: 1,
                                                            background: color + '10', borderRadius: '8px', px: 1.25, py: 1,
                                                            border: `1px solid ${color}30`,
                                                        }}>
                                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, mt: 0.6, flexShrink: 0 }} />
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.78rem', color, lineHeight: 1.3 }}>
                                                                    {label}
                                                                </Typography>
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#94a3b8' }}>
                                                                    {timeStr}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                }
                                                const dot = ev.type === 'pickup' ? '#16a34a' : ev.type === 'skip' ? '#f59e0b' : '#9ca3af';
                                                const verb = ev.type === 'pickup' ? 'Picked up' : ev.type === 'skip' ? 'Skipped' : 'Absent';
                                                const timeStr = ev.ts ? new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
                                                return (
                                                    <Box key={i} sx={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 1,
                                                        background: '#fff', borderRadius: '8px', px: 1.25, py: 1,
                                                        border: '1px solid #f1f5f9',
                                                    }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: dot, mt: 0.6, flexShrink: 0 }} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: '#0f172a', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {ev.stop.firstname} {ev.stop.lastname}
                                                            </Typography>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', color: dot, fontWeight: 600 }}>
                                                                {verb}
                                                            </Typography>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#94a3b8' }}>
                                                                {timeStr} · Stop #{ev.stop.stop_index + 1}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e2e8f0', px: 3, py: 1.5, gap: 1 }}>
                    {!replayLoading && replayPoints.length > 0 && (
                        <Button size="small" onClick={deleteReplayPoints} disabled={replayDeleting}
                            sx={{
                                fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                                color: replayDeleteConfirm ? '#fff' : '#ef4444',
                                background: replayDeleteConfirm ? '#ef4444' : 'transparent',
                                borderRadius: '8px',
                                '&:hover': { background: replayDeleteConfirm ? '#dc2626' : '#fef2f2' },
                            }}>
                            {replayDeleteConfirm ? t('reports.replay_delete_confirm') : t('reports.replay_delete')}
                        </Button>
                    )}
                    {replayPoints.length > 0 && (
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', mr: 'auto' }}>
                            {t('reports.replay_points_count', { count: replayPoints.length })}
                            {` · ${replayIdx + 1} / ${replayPoints.length}`}
                        </Typography>
                    )}
                    <Button size="small" startIcon={<ReplayIcon />} onClick={() => { setReplayIdx(0); setReplayPlaying(false); }}
                        disabled={replayLoading || replayPoints.length === 0}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        {t('reports.replay_reset')}
                    </Button>
                    <Button size="small"
                        startIcon={replayPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        onClick={() => setReplayPlaying(p => !p)}
                        disabled={replayLoading || replayPoints.length === 0}
                        variant="contained"
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: PURPLE, '&:hover': { background: '#7c3aed' } }}>
                        {replayPlaying ? t('reports.replay_pause') : t('reports.replay_play')}
                    </Button>
                    <Button size="small" onClick={closeReplay}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Student details dialog (Daily Report) */}
            <Dialog open={studentDialogOpen} onClose={() => setStudentDialogOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
                    {studentDialogChild?.name}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    {studentDialogChild && (
                        <>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: -1 }}>
                                {formatDate(dailyDate)}
                            </Typography>

                            <Box>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#94a3b8', letterSpacing: '0.04em', mb: 1 }}>
                                    {elUpperCase(t('reports.col_status'))}
                                </Typography>
                                {studentDialogChild.absent ? (
                                    <Chip icon={<PersonOffIcon sx={{ fontSize: 14 }} />} label={t('reports.status_absent')} size="small"
                                        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: RED }} />
                                ) : !studentDialogChild.arrived && !studentDialogChild.departed ? (
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>—</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {studentDialogChild.arrived && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LoginIcon sx={{ fontSize: 16, color: GREEN }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: NAV_BG }}>
                                                    {t('reports.status_arrived')} · {studentDialogChild.arrival_time}
                                                </Typography>
                                            </Box>
                                        )}
                                        {studentDialogChild.departed && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LogoutIcon sx={{ fontSize: 16, color: ORANGE }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: NAV_BG }}>
                                                    {t('reports.status_departed')} · {studentDialogChild.departure_time}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                {studentDialogChild.notes && (
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.75 }}>
                                        {studentDialogChild.notes}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#94a3b8', letterSpacing: '0.04em', mb: 1 }}>
                                    {elUpperCase(t('daily_log.title'))}
                                </Typography>
                                {(studentDialogChild.breakfast > 0 || studentDialogChild.lunch > 0 || studentDialogChild.afternoon > 0 || studentDialogChild.slept || studentDialogChild.comments) ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                            {studentDialogChild.breakfast > 0 && <Chip label={`🍳 ${t('daily_log.breakfast')} ×${studentDialogChild.breakfast}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                            {studentDialogChild.lunch > 0 && <Chip label={`🍽 ${t('daily_log.lunch')} ×${studentDialogChild.lunch}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                            {studentDialogChild.afternoon > 0 && <Chip label={`🍪 ${t('daily_log.afternoon')} ×${studentDialogChild.afternoon}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                            {studentDialogChild.slept && <Chip icon={<BedtimeIcon sx={{ fontSize: 12 }} />} label={t('daily_log.slept')} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                        </Box>
                                        {studentDialogChild.comments && (
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>{studentDialogChild.comments}</Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>{t('attendance.no_log')}</Typography>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setStudentDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>{t('common.close')}</Button>
                    <Button variant="contained" startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate(`/student/${studentDialogChild.id}`)}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                        {t('attendance.view_profile')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
